/**
 * Renewals list (tracker item 85): enrollments with their end date, days left,
 * auto-renew, renewal status and quote total. Filters, row selection with a
 * bulk "Create renewals" action (per-row results), a CSV export and a link to
 * each pending renewal. Creating a renewal schedules and bills nothing.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IBundlesApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { formatJobDate, formatJobMoney } from '../../utils/jobPresentation';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { RENEWAL_STATUS_LABELS, enrollmentPath, saveBlob } from '../../features/service-bundles/bundleUi';

const PAGE_SIZE = 25;

interface Filters {
  bundleId: string;
  year: string;
  daysUntilEnd: string;
  hasRenewal: '' | 'true' | 'false';
  autoRenew: '' | 'true' | 'false';
}

const EMPTY: Filters = { bundleId: '', year: '', daysUntilEnd: '', hasRenewal: '', autoRenew: '' };

/** The API filters for the form values; blank and invalid numbers are left out. */
export function renewalFilters(filters: Filters): types.RenewalListFilters {
  const result: types.RenewalListFilters = {};
  if (filters.bundleId) result.bundleId = filters.bundleId;
  if (/^\d{4}$/.test(filters.year.trim())) result.year = Number(filters.year);
  if (/^-?\d+$/.test(filters.daysUntilEnd.trim())) result.daysUntilEnd = Number(filters.daysUntilEnd);
  if (filters.hasRenewal) result.hasRenewal = filters.hasRenewal === 'true';
  if (filters.autoRenew) result.autoRenew = filters.autoRenew === 'true';
  return result;
}

function daysLeftText(days: number): string {
  if (days < 0) return `Ended ${-days} day${-days === 1 ? '' : 's'} ago`;
  if (days === 0) return 'Today';
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function RenewalsPage() {
  const api = useMemo(() => createAPIClient() as IBundlesApi, []);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<types.RenewalRow[] | null>(null);
  const [pagination, setPagination] = useState<types.RenewalPage['pagination'] | null>(null);
  const [bundles, setBundles] = useState<types.ServiceBundle[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<{ summary: types.BulkRenewalResult; labels: Record<string, string> } | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    api.listServiceBundles().then((list) => { if (active) setBundles(list); }).catch(() => undefined);
    return () => { active = false; };
  }, [api]);

  const apiFilters = useMemo(() => renewalFilters(filters), [filters]);

  useEffect(() => {
    let active = true;
    setRows(null);
    setError(null);
    api.listRenewals({ ...apiFilters, page, limit: PAGE_SIZE })
      .then((result) => { if (active) { setRows(result.data); setPagination(result.pagination); } })
      .catch((err) => { if (active) { setRows([]); setError(getErrorMessage(err, 'Could not load the renewals')); } });
    return () => { active = false; };
  }, [api, apiFilters, page, reload]);

  const update = (patch: Partial<Filters>) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); setSelected(new Set()); };
  const selectable = (rows ?? []).filter((r) => r.renewalStatus === 'NONE');
  const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.id));
  const toggle = (id: string) => setSelected((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map((r) => r.id)));

  const createRenewals = async () => {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const ids = Array.from(selected);
      const labels = Object.fromEntries(ids.map((id) => [id, rowLabel(id)]));
      setResults({ summary: await api.createRenewalsBulk(ids), labels });
      setSelected(new Set());
      setReload((n) => n + 1);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not create the renewals'));
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    setBusy(true);
    setError(null);
    try {
      const file = await api.exportRenewals(apiFilters);
      saveBlob(file.blob, file.filename);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not export the renewals'));
    } finally {
      setBusy(false);
    }
  };

  const rowLabel = useCallback((id: string) => {
    const row = rows?.find((r) => r.id === id);
    return row ? `${row.customer.name}, ${row.bundleName}` : id;
  }, [rows]);

  const pages = Math.max(1, pagination?.pages ?? 1);

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Renewals</h1>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={exportCsv}>Export CSV</button>
          <button type="button" className={primaryButtonClass} disabled={busy || selected.size === 0} onClick={createRenewals}>
            Create renewals{selected.size > 0 ? ` (${selected.size})` : ''}
          </button>
        </div>
      </div>
      <p className="mb-4 text-sm text-gray-600">Service bundles by when their last visit falls. Creating a renewal prepares next season at each visit&apos;s renewal price; nothing is scheduled, billed or sent until a renewal is approved.</p>
      {error && <Toast kind="error">{error}</Toast>}

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-label="Filters">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label htmlFor="renewal-bundle" className={labelClass}>Bundle</label>
            <select id="renewal-bundle" className={inputClass} value={filters.bundleId} onChange={(e) => update({ bundleId: e.target.value })}>
              <option value="">All bundles</option>
              {bundles.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="renewal-year" className={labelClass}>Season (start year)</label>
            <input id="renewal-year" className={inputClass} inputMode="numeric" placeholder="Any" maxLength={4} value={filters.year} onChange={(e) => update({ year: e.target.value })} />
          </div>
          <div>
            <label htmlFor="renewal-days" className={labelClass}>Last visit within (days)</label>
            <input id="renewal-days" className={inputClass} inputMode="numeric" placeholder="Any" value={filters.daysUntilEnd} onChange={(e) => update({ daysUntilEnd: e.target.value })} />
          </div>
          <div>
            <label htmlFor="renewal-has" className={labelClass}>Renewal</label>
            <select id="renewal-has" className={inputClass} value={filters.hasRenewal} onChange={(e) => update({ hasRenewal: e.target.value as Filters['hasRenewal'] })}>
              <option value="">Any</option>
              <option value="false">Not created yet</option>
              <option value="true">Already created</option>
            </select>
          </div>
          <div>
            <label htmlFor="renewal-auto" className={labelClass}>Auto-renew</label>
            <select id="renewal-auto" className={inputClass} value={filters.autoRenew} onChange={(e) => update({ autoRenew: e.target.value as Filters['autoRenew'] })}>
              <option value="">Any</option>
              <option value="true">On</option>
              <option value="false">Off</option>
            </select>
          </div>
        </div>
      </section>

      {results && (
        <section aria-label="Create renewals results" className="mt-4 rounded-lg border border-gray-200 bg-white p-4 sm:p-5" data-testid="bulk-results">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-gray-900">{results.summary.succeeded} of {results.summary.total} renewal{results.summary.total === 1 ? '' : 's'} created{results.summary.failed > 0 ? `, ${results.summary.failed} failed` : ''}</h2>
            <button type="button" className="text-sm text-blue-700 underline" onClick={() => setResults(null)}>Dismiss</button>
          </div>
          <ul className="mt-2 divide-y divide-gray-100 text-sm">
            {results.summary.results.map((r) => (
              <li key={r.enrollmentId} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid="bulk-result-row">
                <span className="break-words">{results.labels[r.enrollmentId] ?? r.enrollmentId}</span>
                {r.success ? (
                  <span className="text-green-700">
                    {r.created === false ? 'Already had a renewal' : 'Renewal created'}
                    {r.renewalId && <> · <Link to={enrollmentPath(r.renewalId)} className="underline">Open</Link></>}
                  </span>
                ) : (
                  <span className="text-red-700">{r.error?.message ?? 'Failed'}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-4 rounded-lg bg-white p-4 shadow sm:p-6">
        {rows === null ? (
          <p role="status" className="text-sm text-gray-500">Loading renewals...</p>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-gray-600">{error ? 'Renewals could not be loaded.' : 'No enrollments match these filters.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-3 text-left">
                    <input type="checkbox" className="h-4 w-4" aria-label="Select all renewals that can be created" checked={allSelected} disabled={selectable.length === 0} onChange={toggleAll} />
                  </th>
                  {['Customer', 'Bundle', 'End date', 'Days left', 'Auto-renew', 'Renewal', 'Quote total'].map((h) => <th key={h} scope="col" className="px-3 py-3 text-left font-medium text-gray-600">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td data-label="Select" className="px-3 py-3">
                      <input type="checkbox" className="h-4 w-4" aria-label={`Select ${row.customer.name}, ${row.bundleName}`} checked={selected.has(row.id)} disabled={row.renewalStatus !== 'NONE'} onChange={() => toggle(row.id)} />
                    </td>
                    <td data-label="Customer" data-primary className="px-3 py-3 font-semibold break-words">
                      <Link to={`/customers/${row.customer.id}`} className="text-blue-700 hover:underline">{row.customer.name}</Link>
                      <span className="block text-xs font-normal text-gray-600">{row.property.address}</span>
                    </td>
                    <td data-label="Bundle" className="px-3 py-3 break-words"><Link to={enrollmentPath(row.id)} className="text-blue-700 hover:underline">{row.bundleName}</Link></td>
                    <td data-label="End date" className="px-3 py-3 whitespace-nowrap">{formatJobDate(row.endDate)}</td>
                    <td data-label="Days left" className="px-3 py-3 whitespace-nowrap">{daysLeftText(row.daysLeft)}</td>
                    <td data-label="Auto-renew" className="px-3 py-3">{row.autoRenew ? 'On' : 'Off'}</td>
                    <td data-label="Renewal" className="px-3 py-3">
                      {row.renewalId ? <Link to={enrollmentPath(row.renewalId)} className="text-blue-700 hover:underline">{RENEWAL_STATUS_LABELS[row.renewalStatus]}</Link> : RENEWAL_STATUS_LABELS[row.renewalStatus]}
                    </td>
                    <td data-label="Quote total" className="px-3 py-3">{formatJobMoney(row.quoteTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span className="text-sm text-gray-600">Page {page} of {pages}</span>
            <button type="button" className={secondaryButtonClass} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </section>
    </main>
  );
}
